import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Store, Loader2, Save, Upload, Image as ImageIcon, MapPin, X, KeyRound, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import AppMap from "@/components/AppMap";
import HoursEditor from "@/components/HoursEditor";
import SpecialHoursEditor from "@/components/SpecialHoursEditor";
import { API } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { DeleteAccountSection } from "@/components/DeleteAccountSection";
import PushToggle from "@/components/PushToggle";
import CoverCropDialog from "@/components/CoverCropDialog";
import StorePreviewDialog from "@/components/StorePreviewDialog";
import CategoryCombobox from "@/components/CategoryCombobox";

const photoUrl = (name, w = 800) => `${API}/maps/photo?name=${encodeURIComponent(name)}&w=${w}`;

function fileToDataUrl(file, maxDim = 800) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = img.width * scale; c.height = img.height * scale;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function StoreSetup() {
  const navigate = useNavigate();
  const { vendor, refreshVendor } = useAuth();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [gq, setGq] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [importing, setImporting] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState("cover_photo");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [draftFound, setDraftFound] = useState(false);
  const hasPassword = (vendor?.auth_provider || "email") === "email";
  const draftKey = vendor ? `kn_store_draft_${vendor.vendor_id}` : null;

  const changePassword = async () => {
    if (hasPassword && !pw.current_password) return toast.error("Inserisci la password attuale");
    if ((pw.new_password || "").trim().length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    setChangingPw(true);
    try {
      await api.put("/vendor/auth/password", {
        current_password: pw.current_password.trim(),
        new_password: pw.new_password.trim(),
      }, { headers: authHeaders("vendor") });
      toast.success("Password aggiornata");
      setPw({ current_password: "", new_password: "" });
      refreshVendor && refreshVendor();
    } catch (err) { toast.error(errMessage(err)); }
    finally { setChangingPw(false); }
  };

  const searchGoogle = async (term) => {
    setGq(term);
    if (term.trim().length < 3) { setSuggestions([]); return; }
    try {
      const r = await api.post("/maps/autocomplete", { input: term.trim() });
      setSuggestions(r.data.suggestions || []);
    } catch (_) { setSuggestions([]); }
  };

  const importPlace = async (place_id) => {
    setImporting(true);
    setSuggestions([]);
    try {
      const r = await api.put("/vendor/google-place", { place_id, use_location: true }, { headers: authHeaders("vendor") });
      const gPhotos = (r.data.photos || []).map((n) => photoUrl(n));
      setForm((f) => ({
        ...f,
        store_name: r.data.name || f.store_name || "",
        address: r.data.address || f.address,
        city: r.data.city || f.city,
        cap: r.data.cap || f.cap,
        province: r.data.province || f.province,
        phone: r.data.phone || f.phone,
        hours: r.data.hours || f.hours,
        hours_struct: r.data.hours_struct || f.hours_struct,
        lat: r.data.lat ?? f.lat,
        lng: r.data.lng ?? f.lng,
        logo: f.logo || (gPhotos[0] ? photoUrl(r.data.photos[0], 400) : ""),
        gallery: [...gPhotos, ...(f.gallery || [])].slice(0, 12),
      }));
      setGq("");
      const msg = gPhotos.length ? `Dati e ${gPhotos.length} foto importati da Google` : "Dati importati da Google";
      toast.success(msg);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import non riuscito"); }
    finally { setImporting(false); }
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const [draftData, setDraftData] = useState(null);
  const firstAutosave = useRef(true);
  useEffect(() => {
    // Seed the form ONCE from the vendor doc. Do NOT re-seed on later `vendor` changes
    // (e.g. background session revalidation or refreshVendor after changing password),
    // otherwise in-progress edits (imported Google data, uploaded cover) get wiped.
    if (!vendor) return;
    const seed = {
      store_name: vendor.store_name || "", category: vendor.category || "altro",
      address: vendor.address || "", city: vendor.city || "", cap: vendor.cap || "",
      province: vendor.province || "", phone: vendor.phone || "", description: vendor.description || "",
      hours: vendor.hours || "", hours_struct: vendor.hours_struct || null, logo: vendor.logo || "", cover_photo: vendor.cover_photo || "",
      lat: vendor.lat ?? 43.9096, lng: vendor.lng ?? 12.9131, gallery: vendor.gallery || [], special_hours: vendor.special_hours || [],
    };
    setForm((prev) => prev || seed);
    // Draft recovery: if a locally-saved draft differs from the saved data, offer to restore it.
    try {
      const raw = draftKey && localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (JSON.stringify(d) !== JSON.stringify(seed)) { setDraftData(d); setDraftFound(true); }
      }
    } catch (_) {}
  }, [vendor]);

  // Autosave the form as a local draft (skips the initial seed write so an existing draft
  // is preserved until the vendor actually edits, restores, or discards it).
  useEffect(() => {
    if (!form || !draftKey) return;
    if (firstAutosave.current) { firstAutosave.current = false; return; }
    try {
      localStorage.setItem(draftKey, JSON.stringify(form));
    } catch (_) {
      try { localStorage.setItem(draftKey, JSON.stringify({ ...form, gallery: [] })); } catch (_) {}
    }
  }, [form, draftKey]);

  const restoreDraft = () => { if (draftData) setForm(draftData); setDraftFound(false); };
  const discardDraft = () => { try { draftKey && localStorage.removeItem(draftKey); } catch (_) {} setDraftFound(false); };

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1600);
    setCropTarget("cover_photo");
    setCropSrc(dataUrl);
    setCropOpen(true);
    e.target.value = "";
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1000);
    setCropTarget("logo");
    setCropSrc(dataUrl);
    setCropOpen(true);
    e.target.value = "";
  };

  const upload = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, key === "logo" ? 400 : 1000);
    setForm((f) => ({ ...f, [key]: dataUrl }));
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const urls = await Promise.all(files.slice(0, 8).map((f) => fileToDataUrl(f, 1000)));
    setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 12) }));
    e.target.value = "";
  };
  const removeGalleryImg = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setError("");
    if (!form.store_name) return setError("Inserisci il nome del negozio.");
    setSaving(true);
    try {
      await api.put("/vendor/store", form, { headers: authHeaders("vendor") });
      try { draftKey && localStorage.removeItem(draftKey); } catch (_) {}
      setDraftFound(false);
      await refreshVendor();
      toast.success("Negozio salvato");
      navigate("/vendor");
    } catch (err) { setError(errMessage(err)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;
  const allTags = cats.flatMap((m) => m.tags.map((t) => ({ ...t, macro: m.label })));

  const requestCategory = async (name) => {
    try {
      const r = await api.post("/vendor/category-requests", { name }, { headers: authHeaders("vendor") });
      const cat = r.data && r.data.category;
      if (!cat) return null;
      setCats((prev) => {
        const next = prev.map((m) => ({ ...m, tags: [...m.tags] }));
        const altro = next.find((m) => m.id === "altro") || next[next.length - 1];
        if (altro && !altro.tags.some((t) => t.id === cat.id)) {
          altro.tags.push({ id: cat.id, label: cat.label, provisional: cat.provisional });
        }
        return next;
      });
      if (r.data.already_exists) toast.success(`Categoria «${cat.label}» già disponibile, selezionata.`);
      else toast.success(`Categoria «${cat.label}» creata e in attesa di conferma dell'admin.`);
      return cat;
    } catch (e) {
      toast.error(errMessage(e) || "Richiesta categoria non riuscita");
      return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 kn-fade-up">
      <div><h1 className="text-2xl font-extrabold text-slate-900">Il mio negozio</h1><p className="text-slate-500">Queste informazioni appaiono nella tua vetrina.</p></div>

      {draftFound && (
        <div className="kn-card p-3.5 flex items-center gap-3 border border-amber-200 bg-amber-50" data-testid="draft-banner">
          <RotateCcw className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-amber-800 text-sm">Bozza non salvata trovata</p>
            <p className="text-xs text-amber-700">Abbiamo trovato modifiche non salvate. Vuoi ripristinarle?</p>
          </div>
          <button onClick={restoreDraft} data-testid="draft-restore" className="shrink-0 h-9 px-3 rounded-lg bg-amber-600 text-white font-bold text-xs">Ripristina</button>
          <button onClick={discardDraft} data-testid="draft-discard" className="shrink-0 h-9 px-3 rounded-lg border border-amber-300 text-amber-700 font-bold text-xs">Ignora</button>
        </div>
      )}

      {/* Cover + logo */}
      <div className="kn-card overflow-hidden">
        <div className="relative h-40 bg-slate-100">
          {form.cover_photo ? (
            <>
              <img src={form.cover_photo} alt="" className="w-full h-full object-cover" />
              <label className="absolute bottom-3 right-3 h-9 px-3 rounded-lg bg-white/90 shadow flex items-center gap-1.5 text-sm font-semibold cursor-pointer">
                <Upload className="h-4 w-4" /> Cambia copertina
                <input type="file" accept="image/*" className="hidden" onChange={onPickCover} data-testid="upload-cover" />
              </label>
            </>
          ) : (
            <label data-testid="upload-cover-empty" className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-400 cursor-pointer hover:bg-slate-50 transition-colors">
              <ImageIcon className="h-7 w-7" />
              <span className="text-sm font-bold text-slate-600">Aggiungi una foto di copertina</span>
              <span className="text-xs">Fai risaltare il tuo negozio · consigliato 16:9</span>
              <input type="file" accept="image/*" className="hidden" onChange={onPickCover} data-testid="upload-cover" />
            </label>
          )}
        </div>
        <div className="p-4 flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
            {form.logo ? <img src={form.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-7 w-7 text-slate-300" />}
          </div>
          <label className="h-9 px-3 rounded-lg border border-slate-200 flex items-center gap-1.5 text-sm font-semibold cursor-pointer">
            <Upload className="h-4 w-4" /> Carica logo
            <input type="file" accept="image/*" className="hidden" onChange={onPickLogo} data-testid="upload-logo" />
          </label>
        </div>
      </div>

      <div className="kn-card p-4 space-y-3.5">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#E63946]" /> Importa da Google</Label>
          <p className="text-xs text-slate-400">Cerca la tua attività per compilare indirizzo, orari e recensioni automaticamente.</p>
          <div className="relative">
            <Input value={gq} onChange={(e) => searchGoogle(e.target.value)} placeholder="Cerca la tua attività su Google..." className="h-11 rounded-xl" data-testid="google-search" />
            {importing && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-[#E63946]" />}
            {suggestions.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 kn-card p-1.5 max-h-64 overflow-y-auto" data-testid="google-suggestions">
                {suggestions.map((s) => (
                  <button key={s.place_id} type="button" onClick={() => importPlace(s.place_id)} className="w-full text-left p-2.5 rounded-lg hover:bg-slate-50">
                    <p className="font-semibold text-slate-800 text-sm">{s.main}</p>
                    <p className="text-xs text-slate-400">{s.secondary}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <Field label="Nome negozio" testid="store-name"><Input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} className="h-11 rounded-xl" data-testid="store-name" /></Field>
        <div className="space-y-1.5">
          <Label>Categoria</Label>
          <CategoryCombobox
            value={form.category}
            options={allTags}
            onChange={(v) => setForm({ ...form, category: v })}
            onRequest={requestCategory}
          />
        </div>
        <Field label="Indirizzo"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 rounded-xl" data-testid="store-address" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Città"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-11 rounded-xl" data-testid="store-city" /></Field>
          <Field label="CAP"><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="h-11 rounded-xl" /></Field>
          <Field label="Prov."><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="h-11 rounded-xl" /></Field>
        </div>
        <Field label="Telefono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl" data-testid="store-phone" /></Field>
        <Field label="Orari di apertura">
          <HoursEditor value={form.hours_struct} onChange={(v) => setForm({ ...form, hours_struct: v })} />
        </Field>
        <Field label="Orari speciali e chiusure (festività, ferie)">
          <SpecialHoursEditor value={form.special_hours} onChange={(v) => setForm({ ...form, special_hours: v })} />
        </Field>
        <Field label="Descrizione"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-xl" /></Field>

        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#E63946]" /> Posizione sulla mappa</Label>
          <p className="text-xs text-slate-400">Trascina il segnaposto per posizionare esattamente il tuo negozio.</p>
          <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="store-map">
            <AppMap
              center={{ lat: form.lat, lng: form.lng }}
              zoom={15}
              draggable
              onDragEnd={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
              style={{ height: "240px" }}
            />
          </div>
          <p className="text-xs text-slate-400">Lat {Number(form.lat).toFixed(4)}, Lng {Number(form.lng).toFixed(4)}</p>
        </div>

        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-[#E63946]" /> Galleria foto del locale</Label>
          <p className="text-xs text-slate-400">Mostra ai clienti l'ambiente, i piatti o i prodotti (max 12 foto).</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="gallery-grid">
            {(form.gallery || []).map((g, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden group">
                <img src={g} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeGalleryImg(i)} data-testid={`gallery-remove-${i}`}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {(form.gallery || []).length < 12 && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-[#E63946] hover:text-[#E63946] transition-colors">
                <Upload className="h-5 w-5" />
                <span className="text-[11px] font-semibold mt-1">Aggiungi</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadGallery} data-testid="gallery-upload" />
              </label>
            )}
          </div>
        </div>
        {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="store-error">{error}</p>}
        <p className="text-xs text-slate-400">Se non tocchi la mappa, la posizione viene stimata da città e indirizzo.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="store-preview-btn" className="flex-1 h-12 rounded-xl font-bold gap-2">
            <Eye className="h-5 w-5" /> Anteprima vetrina
          </Button>
          <Button onClick={save} disabled={saving} data-testid="store-save" className="flex-1 h-12 rounded-xl font-bold gap-2">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Salva negozio
          </Button>
        </div>
      </div>

      <div className="kn-card p-4 space-y-3" data-testid="vendor-settings">
        <div>
          <p className="font-bold text-slate-700 flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#E63946]" /> Account e sicurezza</p>
          <p className="text-sm text-slate-500 mt-0.5">{hasPassword ? "Aggiorna la password del tuo negozio." : "Imposta una password per accedere anche senza Google."}</p>
        </div>
        {hasPassword && (
          <PasswordInput testId="vendor-current-pw" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Password attuale" className="h-11 rounded-xl" />
        )}
        <PasswordInput testId="vendor-new-pw" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Nuova password (min 6)" className="h-11 rounded-xl" />
        <Button variant="outline" onClick={changePassword} disabled={changingPw} data-testid="vendor-change-pw" className="w-full h-11 rounded-xl font-bold">
          {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : (hasPassword ? "Aggiorna password" : "Imposta password")}
        </Button>

        <DeleteAccountSection role="vendor" />
      </div>

      <PushToggle role="vendor" />

      <CoverCropDialog
        open={cropOpen}
        image={cropSrc}
        aspect={cropTarget === "logo" ? 1 : 3}
        outWidth={cropTarget === "logo" ? 512 : 1200}
        cropShape={cropTarget === "logo" ? "round" : "rect"}
        title={cropTarget === "logo" ? "Ritaglia il logo" : "Ritaglia la copertina"}
        subtitle={cropTarget === "logo"
          ? "Trascina e usa lo zoom per centrare il logo: sarà sempre quadrato e ordinato."
          : "Trascina per posizionare e usa lo zoom: la copertina avrà sempre proporzioni ordinate."}
        onOpenChange={setCropOpen}
        onCropped={(url) => setForm((f) => ({ ...f, [cropTarget]: url }))}
      />
      <StorePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} form={form}
        categoryLabel={allTags.find((t) => t.id === form.category)?.label || form.category} />
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
